import React, { useContext, useEffect, useState } from "react";
import Navbar from "../components/Navbar";
import { useAuth0 } from "@auth0/auth0-react";
import { useViewPermission } from "../context/PermissionContext";
import NotificationContext from "../context/NotificationContext";
import Loader from "../components/Loader";
import AccessDenied from "../components/AccessDenied";
import { useMutationLock } from "../hooks/useMutationLock";
import PaginationFooter from "../components/PaginationFooter";
import {
  DEFAULT_PAGE_SIZE,
  normalizeListResponse,
} from "../utils/listPagination";
import * as bugReportsApi from "../api/bugReports";

const SEEN_FILTERS = [
  { value: "all", label: "All" },
  { value: "unseen", label: "Unseen" },
  { value: "seen", label: "Seen" },
];

function formatReportedAt(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return String(value);
  }
}

function labelize(value) {
  if (!value) return "—";
  return String(value).charAt(0).toUpperCase() + String(value).slice(1);
}

export default function AdminBugReports() {
  const [reports, setReports] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const limit = DEFAULT_PAGE_SIZE;
  const [loading, setLoading] = useState(true);
  const [seenFilter, setSeenFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");

  const { isAuthenticated, isLoading: authLoading } = useAuth0();
  const notificationCtx = useContext(NotificationContext);
  const { hasPermission, loading: permLoading } =
    useViewPermission("view_bug_reports");
  const { hasPermission: canEdit } = useViewPermission("edit_bug_reports");
  const { busy, run } = useMutationLock();

  const buildFilters = (seenValue = seenFilter) => {
    const filters = {};
    if (seenValue === "seen") filters.seen = true;
    if (seenValue === "unseen") filters.seen = false;
    return filters;
  };

  const fetchReports = async (pageArg = page, opts = {}) => {
    const nextSeen = opts.seenFilter ?? seenFilter;
    const nextSearch = opts.search !== undefined ? opts.search : search;
    try {
      const response = await bugReportsApi.list({
        page: pageArg,
        limit,
        filters: buildFilters(nextSeen),
        search: nextSearch || undefined,
      });
      const normalized = normalizeListResponse(response.data, pageArg, limit);
      setReports(normalized.data);
      setPage(normalized.page);
      setTotal(normalized.total);
      setTotalPages(normalized.totalPages);
    } catch (err) {
      console.error("Error fetching bug reports:", err);
      notificationCtx.show({
        message: "Failed to load bug reports.",
        stat: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    fetchReports(newPage);
  };

  useEffect(() => {
    if (!hasPermission) return;
    fetchReports(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasPermission]);

  const handleSeenFilterChange = (value) => {
    setSeenFilter(value);
    setPage(1);
    fetchReports(1, { seenFilter: value });
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    const next = searchInput.trim();
    setSearch(next);
    setPage(1);
    fetchReports(1, { search: next });
  };

  const handleToggleSeen = (report) => {
    if (!canEdit) return;
    run(async () => {
      try {
        const nextSeen = !report.seen;
        await bugReportsApi.setSeen(report.id, nextSeen);
        setReports((prev) =>
          prev.map((row) =>
            row.id === report.id ? { ...row, seen: nextSeen } : row,
          ),
        );
        notificationCtx.show({
          message: nextSeen ? "Marked as seen." : "Marked as unseen.",
          stat: "success",
        });
      } catch (error) {
        console.error("Failed to update seen:", error);
        notificationCtx.show({
          message: "Failed to update report.",
          stat: "error",
        });
      }
    });
  };

  if (authLoading || permLoading || loading) {
    return (
      <div className="text-center">
        <Navbar />
        <Loader />
      </div>
    );
  }

  if (!isAuthenticated || !hasPermission) {
    return (
      <div className="text-center">
        <Navbar />
        <AccessDenied />
      </div>
    );
  }

  return (
    <div>
      <Navbar />
      {isAuthenticated && hasPermission && (
        <div
          className="d-flex flex-column"
          style={{
            height: "109vh",
            minHeight: 0,
            padding: "10px 50px 0",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              backgroundColor: "#d5d1fd",
              borderRadius: "20px",
              padding: "26px 30px",
              flexShrink: 0,
              boxShadow: "0 2px 10px rgba(48,45,69,0.04)",
            }}
          >
            <div
              className="d-flex justify-content-between align-items-start flex-wrap gap-4"
            >
              <div style={{ minWidth: 0, paddingTop: "2px" }}>
                <h1
                  className="mb-1"
                  style={{
                    fontWeight: "700",
                    color: "#302d45",
                  }}
                >
                  BUG REPORTS
                </h1>
                <small style={{ color: "#625d75", fontSize: "13px" }}>
                  Review issues submitted by students
                </small>
              </div>

              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "12px",
                  flex: "1 1 280px",
                  maxWidth: "420px",
                  marginLeft: "auto",
                }}
              >
                <div>
                  <div
                    style={{
                      fontSize: "11px",
                      fontWeight: 700,
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                      color: "#625d75",
                      marginBottom: "8px",
                    }}
                  >
                    Status
                  </div>
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    {SEEN_FILTERS.map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        className="btn"
                        onClick={() => handleSeenFilterChange(opt.value)}
                        style={{
                          height: "38px",
                          display: "flex",
                          alignItems: "center",
                          backgroundColor:
                            seenFilter === opt.value ? "#fff" : "transparent",
                          color: "#403b55",
                          border:
                            seenFilter === opt.value
                              ? "none"
                              : "1px solid rgba(64,59,85,0.2)",
                          padding: "0 14px",
                          borderRadius: "8px",
                          fontWeight: "600",
                          fontSize: "13px",
                        }}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <div
                    style={{
                      fontSize: "11px",
                      fontWeight: 700,
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                      color: "#625d75",
                      marginBottom: "8px",
                    }}
                  >
                    Search
                  </div>
                  <form
                    onSubmit={handleSearchSubmit}
                    className="d-flex align-items-center gap-2"
                  >
                    <input
                      type="search"
                      className="form-control"
                      placeholder="Email or message"
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                      style={{
                        height: "38px",
                        flex: 1,
                        borderRadius: "8px",
                        border: "none",
                      }}
                    />
                    <button
                      type="submit"
                      className="btn"
                      style={{
                        height: "38px",
                        backgroundColor: "#fff",
                        color: "#403b55",
                        border: "none",
                        borderRadius: "8px",
                        fontWeight: "600",
                        fontSize: "13px",
                        padding: "0 14px",
                        flexShrink: 0,
                      }}
                    >
                      Search
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </div>

          <div style={{ height: "16px", flexShrink: 0 }} />

          {reports.length === 0 ? (
            <div
              className="d-flex flex-column align-items-center justify-content-center"
              style={{
                flex: 1,
                minHeight: 0,
                color: "#77718b",
              }}
            >
              <div
                style={{
                  fontSize: "18px",
                  fontWeight: "600",
                  color: "#403b55",
                }}
              >
                No bug reports found
              </div>
              <small style={{ marginTop: "5px", fontSize: "13px" }}>
                Student reports will appear here when submitted.
              </small>
            </div>
          ) : (
            <>
              <div
                style={{
                  flex: 1,
                  minHeight: 0,
                  overflow: "hidden",
                  marginTop: "10px",
                  backgroundColor: "#fff",
                  borderRadius: "14px",
                  border: "1px solid #eeeaf8",
                  boxShadow: "0 2px 10px rgba(48,45,69,0.05)",
                }}
              >
                <div style={{ height: "100%", overflow: "auto" }}>
                  <table
                    className="table align-middle mb-0"
                    style={{
                      minWidth: "900px",
                      width: "100%",
                      background: "#fff",
                      borderCollapse: "separate",
                      borderSpacing: 0,
                    }}
                  >
                    <thead>
                      <tr>
                        {[
                          "Email",
                          "Device",
                          "Browser",
                          "Message",
                          "Reported",
                          "Status",
                          "Actions",
                        ].map((label) => (
                          <th
                            key={label}
                            style={{
                              position: "sticky",
                              top: 0,
                              zIndex: 5,
                              background: "#d5d1fd",
                              color: "#302d45",
                              fontWeight: "600",
                              padding: "13px 14px",
                              textAlign: "center",
                              letterSpacing: "0.4px",
                              fontSize: "13px",
                              borderBottom: "1px solid #e6e3f3",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {label.toUpperCase()}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {reports.map((item) => (
                        <tr key={item.id}>
                          <td
                            style={{
                              background: "#fff",
                              padding: "12px 14px",
                              textAlign: "center",
                              fontSize: "14px",
                              color: "#403b55",
                              borderBottom: "1px solid #f0edf6",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {item.reporterEmail}
                          </td>
                          <td
                            style={{
                              background: "#fff",
                              padding: "12px 14px",
                              textAlign: "center",
                              fontSize: "14px",
                              color: "#403b55",
                              borderBottom: "1px solid #f0edf6",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {labelize(item.device)}
                          </td>
                          <td
                            style={{
                              background: "#fff",
                              padding: "12px 14px",
                              textAlign: "center",
                              fontSize: "14px",
                              color: "#403b55",
                              borderBottom: "1px solid #f0edf6",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {labelize(item.browser)}
                          </td>
                          <td
                            style={{
                              background: "#fff",
                              padding: "12px 14px",
                              textAlign: "left",
                              fontSize: "14px",
                              color: "#403b55",
                              borderBottom: "1px solid #f0edf6",
                              maxWidth: "320px",
                              whiteSpace: "normal",
                              wordBreak: "break-word",
                            }}
                          >
                            {item.message}
                          </td>
                          <td
                            style={{
                              background: "#fff",
                              padding: "12px 14px",
                              textAlign: "center",
                              fontSize: "13px",
                              color: "#403b55",
                              borderBottom: "1px solid #f0edf6",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {formatReportedAt(item.createdAt)}
                          </td>
                          <td
                            style={{
                              background: "#fff",
                              padding: "12px 14px",
                              textAlign: "center",
                              borderBottom: "1px solid #f0edf6",
                            }}
                          >
                            <span
                              style={{
                                display: "inline-block",
                                padding: "4px 10px",
                                borderRadius: "8px",
                                fontSize: "12px",
                                fontWeight: 600,
                                backgroundColor: item.seen
                                  ? "#bff1db"
                                  : "#f9e1be",
                                color: "#403b55",
                              }}
                            >
                              {item.seen ? "Seen" : "Unseen"}
                            </span>
                          </td>
                          <td
                            style={{
                              textAlign: "center",
                              padding: "10px 12px",
                              borderBottom: "1px solid #f0edf6",
                            }}
                          >
                            {canEdit && (
                              <button
                                type="button"
                                className="btn"
                                disabled={busy}
                                onClick={() => handleToggleSeen(item)}
                                style={{
                                  backgroundColor: item.seen
                                    ? "#f2c4c4"
                                    : "#bff1db",
                                  border: "none",
                                  borderRadius: "8px",
                                  height: "34px",
                                  padding: "0 12px",
                                  fontWeight: 600,
                                  fontSize: "12px",
                                  color: "#403b55",
                                }}
                              >
                                {item.seen ? "Mark unseen" : "Mark seen"}
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <PaginationFooter
                page={page}
                limit={limit}
                total={total}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                itemLabel="reports"
              />
            </>
          )}
        </div>
      )}
    </div>
  );
}
